/**
 * Bloco do limite calculado da sílica livre (#459)
 *
 * Compartilhado pelo formulário de medição e pelo passo de medições da janela de risco: as duas
 * telas mostram a mesma coisa, e escrevê-las à parte divergiria na primeira mudança.
 *
 * Quatro peças, nesta ordem: o % de quartzo (a única entrada), o limite calculado com a fórmula
 * por extenso, o nível de ação e a faixa de situação — sempre em texto, com a cor só reforçando.
 */
import React from 'react';
import { Alert, Box, TextField, Typography } from '@mui/material';
import type { AlertColor } from '@mui/material';
import type {
  FormulaDoLimiteParaTela,
  SituacaoDaMedicao,
} from '../../types/environmentalMeasurement';
import {
  calcularLimiteDaSilica,
  descreverFormula,
  formatarNumeroDaMedicao,
  nivelDeAcaoDoLimite,
  situacaoDaMedicao,
} from './limiteDaSilica';

const FAIXAS: Record<SituacaoDaMedicao, { gravidade: AlertColor; titulo: string; explicacao: string }> = {
  ABAIXO_DA_ACAO: {
    gravidade: 'success',
    titulo: 'Abaixo do nível de ação',
    explicacao: 'O resultado está longe do limite.',
  },
  ACIMA_DA_ACAO: {
    gravidade: 'warning',
    titulo: 'Acima do nível de ação, dentro do limite',
    explicacao: 'O resultado passou da metade do limite. Vale acompanhar.',
  },
  ACIMA_DO_LIMITE: {
    gravidade: 'error',
    titulo: 'Acima do limite de tolerância',
    explicacao: 'O resultado passou do limite de tolerância.',
  },
};

export interface BlocoDoLimiteCalculadoProps {
  /**
   * Parâmetros da fórmula do risco. Sem eles (edição sem acesso ao risco) não há prévia: o bloco
   * diz que o limite será recalculado ao salvar e mostra, rotulado, o que já está gravado.
   */
  formula: FormulaDoLimiteParaTela | null;
  /** O que foi digitado em "% de quartzo": texto, porque campo vazio é ausência do dado */
  quartzo: string;
  onQuartzoChange: (texto: string) => void;
  /** O resultado do laboratório, também como texto digitado */
  resultado: string;
  /** Limite que o servidor gravou, mostrado como "gravado" quando a fórmula não chegou à tela */
  limiteGravado?: number | null;
  /** Unidade que a fórmula define, ou a gravada na medição */
  unidade?: string | null;
  erro?: string;
  disabled?: boolean;
}

const numeroDoTexto = (texto: string): number | null => {
  if (texto.trim() === '') return null;
  const numero = Number(texto);
  return Number.isFinite(numero) ? numero : null;
};

export const BlocoDoLimiteCalculado: React.FC<BlocoDoLimiteCalculadoProps> = ({
  formula,
  quartzo,
  onQuartzoChange,
  resultado,
  limiteGravado = null,
  unidade = null,
  erro,
  disabled = false,
}) => {
  const quartzoNumerico = numeroDoTexto(quartzo);
  const quartzoValido = quartzoNumerico !== null && quartzoNumerico >= 0 && quartzoNumerico <= 100;
  const unidadeDoLimite = formula?.unidade ?? unidade ?? '';

  const limite = formula && quartzoValido ? calcularLimiteDaSilica(formula, quartzoNumerico) : null;

  const valor = numeroDoTexto(resultado);
  const situacao =
    limite !== null && valor !== null && valor > 0 ? situacaoDaMedicao({ valor, limite }) : null;
  const faixa = situacao ? FAIXAS[situacao] : null;

  const comUnidade = (numero: number): string =>
    [formatarNumeroDaMedicao(numero), unidadeDoLimite].filter(Boolean).join(' ');

  const ajudaDoQuartzo = (): string => {
    if (erro) return erro;
    if (quartzo.trim() !== '' && !quartzoValido) return 'Informe um número de 0 a 100.';
    return 'Quanto de quartzo o laudo encontrou na poeira, de 0 a 100. O limite depende dele.';
  };

  return (
    <Box data-testid="bloco-do-limite-calculado">
      <TextField
        label="% de quartzo"
        type="number"
        size="small"
        value={quartzo}
        onChange={(evento) => onQuartzoChange(evento.target.value)}
        disabled={disabled}
        error={Boolean(erro) || (quartzo.trim() !== '' && !quartzoValido)}
        helperText={ajudaDoQuartzo()}
        inputProps={{ step: 'any', min: 0, max: 100 }}
        sx={{ maxWidth: 260 }}
      />

      <Box sx={{ mt: 2 }}>
        {!formula ? (
          <>
            <Typography variant="body2" color="text.secondary">
              O limite será recalculado ao salvar.
            </Typography>
            {limiteGravado !== null && (
              <Typography variant="body2" color="text.secondary">
                Limite gravado: {comUnidade(limiteGravado)}
              </Typography>
            )}
          </>
        ) : limite !== null ? (
          <>
            <Typography variant="body1" fontWeight="medium">
              Limite de tolerância calculado: {comUnidade(limite)}
            </Typography>
            {formula && (
              <Typography variant="body2" color="text.secondary">
                Calculado assim: {descreverFormula(formula)}
              </Typography>
            )}
            <Typography variant="body2" color="text.secondary">
              Nível de ação (metade do limite): {comUnidade(nivelDeAcaoDoLimite(limite))}
            </Typography>
          </>
        ) : (
          <Typography variant="body2" color="text.secondary">
            Informe o % de quartzo para ver o limite. A conta é {descreverFormula(formula)}.
          </Typography>
        )}
      </Box>

      {faixa ? (
        <Alert severity={faixa.gravidade} sx={{ mt: 2 }}>
          <strong>{faixa.titulo}.</strong> {faixa.explicacao}
        </Alert>
      ) : (
        limite !== null && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            Informe o valor medido para ver em que faixa o resultado ficou.
          </Typography>
        )
      )}
    </Box>
  );
};

export default BlocoDoLimiteCalculado;
